import { TEST_TENANT_IDS } from './seedTenants';
import { TEST_E2E_TENANT_ID, TEST_MODE_BILLING_TENANT_IDS } from './testModeTenants';

/**
 * Cadastro dos tenants de TESTE de produção: quais são e para que serve cada um.
 *
 * Origem: #1006 — 2026-10-19 18:04 +0200 (owner: a lista do admin mostrava os
 * tenants de teste como "Ativo"/"Demo"/"Suspenso", e ninguém sabia dizer para
 * que servia cada um — três sobras da #970 e um cadastro antigo ficaram órfãos).
 *
 * Por quê: até aqui "é teste" vivia espalhado em duas listas com efeito
 * colateral — `TEST_TENANT_IDS` (`seedTenants.ts`) liga o simulador de uso, e
 * `TEST_MODE_BILLING_TENANT_IDS` (`testModeTenants.ts`) liga o gateway
 * sandbox. Um tenant de teste que não deve ter nenhum dos dois (a conta da App
 * Review, uma sobra de e2e) não tinha onde ser marcado. Este cadastro só
 * CLASSIFICA: não liga simulador nem sandbox, e por isso incluir um id aqui
 * não exige o aval que as outras duas listas exigem.
 *
 * Contrato: `Record` fechado por id. Todo id das duas listas acima TEM de estar
 * aqui — `testTenants.test.ts` cobra, para as três não divergirem. `keep:
 * false` marca o tenant que o owner pode apagar; ao apagar, o id sai daqui no
 * mesmo commit.
 *
 * Usado por: `isTestTenant` (`tenantDisplayStatus.ts`) → status "Teste" da
 * coluna Assinatura do admin (`TenantDisplayStatusBadge`), mapa de cidades do
 * CRM (`crmCities.ts`), relatório de inativos (`dailyInactiveTenantsReport.ts`)
 * e monitor de uso (`detectTenantUsageAnomalies.ts`). Roteiro humano:
 * `docs/test-tenants-runbook.md`.
 *
 * Arquitetura: sem `zod` de propósito, como `seedTenants.ts` — scripts do repo
 * importam com `tsx`, direto do source. O predicado que também olha o
 * `livemode` da assinatura mora em `tenantDisplayStatus.ts`, que já depende de
 * `billing.ts`.
 *
 * Dicas:
 *  - Tenant sandbox criado por e2e (`scripts/e2e-self-service` cria um por
 *    rodada) NÃO precisa entrar aqui: a assinatura `livemode: false` já o
 *    classifica como teste.
 *  - Nunca reativar nem apagar o `APP_REVIEW`: a Apple pediu uma conta com a
 *    assinatura VENCIDA para revisar a compra (#978). O `appStoreDailyReconcile`
 *    (05:30 SP) aplica o status que a Apple sandbox devolver: se alguém
 *    reassinar com o Apple ID sandbox desta conta, o tenant volta a ACTIVE e
 *    deixa de servir à revisão — expire de novo em Ajustes › Sandbox.
 *
 * Issues: #1006 (origem), #1003 (testes × demos), #978 (App Review), #1005
 * (`BR-test-e2e`), #970 (sobras do teste de estabelecimento adicional), #1007
 * (o e2e de self-service apaga o que cria).
 *
 * Histórico:
 *  - 2026-10-19 18:04 +0200 — (#1006) cadastro criado com os 10 ids.
 *  - 2026-10-27 13:34 +0100 — (#1006, owner) apagados em produção os tenants
 *    `LEFTOVER` (três sobras `IT-…` da #970 e o cadastro BR do onboarding antigo)
 *    e o documento do `BR-88888888000191`; os quatro `LEFTOVER` saíram daqui.
 *    Os testes passaram a rodar na Super Suíte e no slot `full` do Docker (#1011).
 */

/** Para que o tenant de teste existe. */
export const TestTenantPurpose = {
  /** Bancada dos aparelhos de QA (Pixel 8 / iPhone 12, login `android@`). */
  DEVICE_BENCH: 'DEVICE_BENCH',
  /** QA manual + simulador de uso agendado. */
  SIMULATOR_QA: 'SIMULATOR_QA',
  /** Conta de demonstração entregue à App Review da Apple (#978). */
  APP_REVIEW: 'APP_REVIEW',
  /** Tenant que o e2e de venda (`scripts/e2e-asaas`, `scripts/e2e-sale`) usa. */
  E2E_SALE: 'E2E_SALE',
  /** Id que só existe em fixture de teste unitário — não há documento em prod. */
  UNIT_FIXTURE: 'UNIT_FIXTURE',
  /** Sobra de teste já encerrado, sem uso — o owner pode apagar. */
  LEFTOVER: 'LEFTOVER',
} as const;
export type TestTenantPurpose = (typeof TestTenantPurpose)[keyof typeof TestTenantPurpose];

/** Ficha de um tenant de teste. */
export type TestTenantEntry = {
  readonly purpose: TestTenantPurpose;
  /** Quem usa, em texto livre para quem lê o código e o roteiro. */
  readonly usedBy: string;
  /** `false` = sem uso; o owner pode apagar (e o id sai daqui junto). */
  readonly keep: boolean;
  /** Issue onde o tenant nasceu ou foi decidido. */
  readonly issue: string;
};

/**
 * As fichas, levantadas em produção em 2026-10-19 (#1006) e revistas em
 * 2026-10-27, quando as sobras foram apagadas. Tenant sandbox `{CC}-{autoId}`
 * (ex.: o que o revisor da Apple cria ao assinar) não precisa entrar: a
 * assinatura `livemode: false` já o classifica.
 */
export const TEST_TENANT_REGISTRY: Readonly<Record<string, TestTenantEntry>> = {
  'IT-98765432103': {
    purpose: TestTenantPurpose.DEVICE_BENCH,
    usedBy: 'Pixel 8 / iPhone 12 (android@ OWNER), simulador de uso, billing sandbox',
    keep: true,
    issue: '#1003',
  },
  'BR-01010001000180': {
    purpose: TestTenantPurpose.SIMULATOR_QA,
    usedBy: 'QA manual BR, simulador de uso',
    keep: true,
    issue: '#1003',
  },
  'IT-f5OcROxAFTTFTwdoyk94': {
    purpose: TestTenantPurpose.APP_REVIEW,
    usedBy: 'App Store Connect — conta demo appreview@, assinatura vencida de propósito',
    keep: true,
    issue: '#978',
  },
  [TEST_E2E_TENANT_ID]: {
    purpose: TestTenantPurpose.E2E_SALE,
    usedBy: 'scripts/e2e-asaas, scripts/e2e-sale (nasce na primeira rodada após a #1005)',
    keep: true,
    issue: '#1005',
  },
  'BR-88888888000191': {
    purpose: TestTenantPurpose.E2E_SALE,
    usedBy: 'legado do e2e de venda — substituído por BR-test-e2e; documento apagado',
    // 2026-10-27 13:34 +0100 (#1006, owner): o documento foi apagado pela
    // `hardDeleteEmptyTenant`, depois de cancelar a assinatura no Asaas sandbox.
    // O id continua aqui e em `TEST_MODE_BILLING_TENANT_IDS` para que um webhook
    // sandbox atrasado, ou a retomada do reset do `scripts/e2e-asaas`
    // (`LEGACY_TEST_TENANT_ID`), continue sendo tratado como teste.
    keep: false,
    issue: '#752',
  },
  'IT-00000000000': {
    purpose: TestTenantPurpose.UNIT_FIXTURE,
    usedBy: 'fixtures de teste unitário (createSelfServiceCheckout, CRM)',
    keep: true,
    issue: '#258',
  },
};

/**
 * O id está no cadastro (ou numa das duas listas que ele precisa conter)?
 * Não olha a assinatura — para isso, `isTestTenant` em `tenantDisplayStatus.ts`.
 *
 * Origem: #1006 — 2026-10-19 18:04 +0200.
 */
export function isRegisteredTestTenant(tenantId: string): boolean {
  return (
    Object.prototype.hasOwnProperty.call(TEST_TENANT_REGISTRY, tenantId) ||
    (TEST_TENANT_IDS as readonly string[]).includes(tenantId) ||
    (TEST_MODE_BILLING_TENANT_IDS as readonly string[]).includes(tenantId)
  );
}
